import type { ComponentProps } from "react";
import { useCallback } from "react";
import styled from "styled-components";
import { GQLQueryOrderInput } from "@rx-cs-system/schema";

import {
  DataListBody,
  DataListBodyItem,
  DataListHeader,
  DataListHeaderItem,
  DataListRow,
  DataTable,
  SwitchHeaderOrder,
} from "./Table";

import { Typography } from "./Typography";
import { hexToRGB } from "../../utils/style";
import { DeliveryOrderProducts } from "../../types";

type ColumnName = "productName" | "serialNumber" | "quantity";

export type Props = {
  products: DeliveryOrderProducts[];
  order?: GQLQueryOrderInput;
  onSortDetailClick: (input: GQLQueryOrderInput) => void;
};

const ContentWrapper = styled.div`
  padding: 0 40px;
`;

const Inner = styled.div`
  padding: 40px 0 20px;
  display: flex;
  ${(props) => hexToRGB(props.theme.colors.primaryBorder, 0.5)};
`;

const PaddingRightWrapper = styled.div`
  width: 100%;
  padding-right: 20px;
`;

const TextRightTypography = (props: ComponentProps<typeof Typography>) => (
  <PaddingRightWrapper>
    <Typography textRight size={2}>
      {props.children}
    </Typography>
  </PaddingRightWrapper>
);

export function DeliveryOrderDetailProduct({
  products,
  order,
  onSortDetailClick,
}: Props) {
  const sortClick = useCallback(
    (value: ColumnName) => {
      onSortDetailClick(SwitchHeaderOrder(value, order));
    },
    [onSortDetailClick, order],
  );

  const header = (
    <>
      <DataListHeaderItem
        onSort={() => sortClick("productName")}
        order={order?.field === "productName" ? order.value : undefined}
      >
        <Typography size={1} bold>
          物品名
        </Typography>
      </DataListHeaderItem>
      <DataListHeaderItem
        onSort={() => sortClick("serialNumber")}
        order={order?.field === "serialNumber" ? order.value : undefined}
      >
        <Typography size={1} bold>
          シリアルNo
        </Typography>
      </DataListHeaderItem>
      <DataListHeaderItem
        onSort={() => sortClick("quantity")}
        order={order?.field === "quantity" ? order.value : undefined}
        align="flex-end"
      >
        <Typography size={1} bold>
          個数
        </Typography>
      </DataListHeaderItem>
    </>
  );

  return (
    <ContentWrapper>
      <Inner>
        <DataTable>
          <DataListHeader row={header} flexRatio={[4, 4, 3]} />
          <DataListBody
            rows={products.map((deliveryOrderProduct) => (
              <DataListRow flexRatio={[4, 4, 3]}>
                <DataListBodyItem>
                  <Typography size={2} withEllipsis={true}>
                    {deliveryOrderProduct.product.name}
                  </Typography>
                </DataListBodyItem>
                <DataListBodyItem>
                  <Typography size={2} withEllipsis={true}>
                    {deliveryOrderProduct.serialNumber || "-"}
                  </Typography>
                </DataListBodyItem>
                <DataListBodyItem>
                  <TextRightTypography>
                    {deliveryOrderProduct.quantity}
                  </TextRightTypography>
                </DataListBodyItem>
              </DataListRow>
            ))}
          />
        </DataTable>
      </Inner>
    </ContentWrapper>
  );
}
